<template>
  <SectionPageShell section="websites" title="Websites">
    <div v-if="hasWebsites" class="row">
        <DataTable
            :paginator="true"
            :rows="20"
            :totalRecords="items.length"
            dataKey="id"
            :value="items"
            :loading="websiteStore.loading"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[10,20,50]"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            responsiveLayout="scroll">
          <TableColumn field="url" header="Link" />
          <TableColumn header="Typ">
            <template #body="{data}">
              <div class="position-relative d-inline-flex align-items-center">
                <button
                  type="button"
                  class="type-indicator-btn"
                  :title="getTypeMeta(data.product_type).label"
                  @click="togglePopover($event, data.id)"
                >
                  <Icon :icon="getTypeMeta(data.product_type).icon" :class="getTypeMeta(data.product_type).colorClass" />
                </button>
                <div v-if="openPopoverId === data.id" ref="popover" class="website-type-popover" :style="popoverStyle">
                  <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <strong class="d-block">{{ getTypeMeta(data.product_type).label }}</strong>
                    <button type="button" class="popover-close-btn" @click="closePopover" aria-label="Schließen">
                      <Icon icon="fa-pro-solid:xmark" />
                    </button>
                  </div>
                  <small>{{ getTypeMeta(data.product_type).text }}</small>
                  <div v-if="getTypeMeta(data.product_type).showSupportLink" class="mt-2">
                    <small>
                      Gern beraten wir Sie zu einem Website-Relaunch.
                      <router-link :to="{name: 'account.support'}" @click="closePopover">Kontaktieren Sie uns jederzeit!</router-link>
                    </small>
                  </div>
                </div>
              </div>
            </template>
          </TableColumn>
          <TableColumn header="Aktionen">
            <template #body="{data}">
              <div class="d-flex align-items-center">
                <router-link class="me-3 d-inline-flex align-items-center" title="Statistik anzeigen" :to="{name: 'websites.stats', params: {website_id: data.id}}">
                  <ActionIcon icon="fa-pro-solid:chart-pie" />
                </router-link>
                <a class="d-inline-flex align-items-center" :href="data.url" target="_blank" rel="noopener noreferrer" title="Website öffnen">
                  <ActionIcon icon="fa-pro-solid:up-right-from-square" />
                </a>
              </div>
            </template>
          </TableColumn>
        </DataTable>
    </div>
    <section v-else class="website-offer mt-3">
      <div class="website-offer__hero">
        <p class="website-offer__eyebrow">Noch keine Website hinterlegt</p>
        <h3 class="website-offer__title">Ihre neue Website als digitales Aushängeschild</h3>
        <p class="website-offer__lead mb-0">
          Gewinnen Sie mehr Sichtbarkeit und Vertrauen mit einer professionellen Website von MANET.
          Modern, suchmaschinenoptimiert, barrierearm und datenschutzfreundlich.
        </p>
      </div>

      <div class="website-offer__pillars">
        <article class="website-offer__pillar">
          <h4>Für Ihre Branche gemacht</h4>
          <p class="mb-0">Lösungen für Freiberufler, Dienstleister, Handwerk und Tourismusanbieter.</p>
        </article>
        <article class="website-offer__pillar">
          <h4>Technisch stark</h4>
          <p class="mb-0">Responsive Design, SEO-Basics, schnelle Ladezeiten, Hosting, SSL, Wartung und Backups inklusive.</p>
        </article>
        <article class="website-offer__pillar">
          <h4>Rechtlich sicher</h4>
          <p class="mb-0">Datensparsame Umsetzung mit Fokus auf DSGVO und Barrierefreiheit nach WCAG/BFSG.</p>
        </article>
      </div>

      <div class="website-offer__pricing">
        <div class="website-offer__price-card">
          <p class="website-offer__price-label mb-1">Website-Paket</p>
          <p class="website-offer__price-main mb-1">
            <PriceAmount label="ab 999 €" block />
          </p>
          <p class="website-offer__price-sub mb-0">
            zzgl.
            <PriceAmount label="25 €" suffix="monatlich für Betrieb & Wartung" inline />
          </p>
        </div>
        <ul class="website-offer__facts mb-0">
          <li>WordPress-basiert mit individueller Gestaltung</li>
          <li>Inhalte bleiben vollständig in Ihrer Hand</li>
          <li>Persönliche Beratung von der Planung bis zum Go-Live</li>
        </ul>
      </div>

      <div class="website-offer__cta">
        <router-link :to="supportCreateRoute" class="btn btn-success btn-lg">
          Angebot für Website-Erstellung anfragen
        </router-link>
        <router-link :to="{name: 'account.support'}" class="btn btn-outline-secondary btn-lg">
          Support kontaktieren
        </router-link>
      </div>
    </section>
  </SectionPageShell>
</template>

<script>
import ActionIcon from "@/components/general/ActionIcon.vue";
import PriceAmount from "@/components/general/PriceAmount.vue";
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useWebsitesStore} from "@/stores/websites";
import {useUserStore} from "@/stores/user";
import dayjs from "dayjs";

export default {
    name: "WebsitesIndex",
    components: {
      ActionIcon,
      PriceAmount,
      SectionPageShell,
    },
    data() {
      return {
        items: [],
        openPopoverId: null,
        popoverStyle: {},
      }
    },
    setup() {
        return {
            websiteStore: useWebsitesStore(),
            userStore: useUserStore(),
        }
    },
    computed: {
      hasWebsites() {
        return this.items.length > 0
      },
      supportCreateRoute() {
        return {
          name: 'account.support.create',
          query: {
            title: `Kdnr: ${this.getKdnr()} - Anfrage Angebot Website`,
          },
        }
      },
    },
    created() {
        this.websiteStore.index().then(({data}) => {
          this.items = data.websites
        })
    },
    mounted() {
      document.addEventListener('click', this.handleDocumentClick)
    },
    unmounted() {
      document.removeEventListener('click', this.handleDocumentClick)
    },
    methods: {
      togglePopover(event, id) {
        event.stopPropagation()
        if (this.openPopoverId === id) {
          this.openPopoverId = null
          return
        }

        const rect = event.currentTarget.getBoundingClientRect()
        this.popoverStyle = {
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          left: `${Math.min(rect.left, window.innerWidth - 320)}px`,
        }
        this.openPopoverId = id
      },
      closePopover() {
        this.openPopoverId = null
      },
      handleDocumentClick(event) {
        if (this.openPopoverId === null) {
          return
        }

        const popover = this.$refs.popover
        const activePopover = Array.isArray(popover) ? popover[0] : popover
        if (activePopover?.contains(event.target)) {
          return
        }

        this.closePopover()
      },
      getKdnr() {
        return this.$route.query.kdnr || this.userStore?.user?.organisation?.kdnr || ''
      },
      getTypeMeta(type) {
        const yearsSince2018 = dayjs().year() - 2018
        const yearsSince2014 = dayjs().year() - 2014

        switch (type) {
          case 'basis3-portal':
            return {
              label: 'Aktuelle Generation',
              colorClass: 'text-success',
              icon: 'fa-pro-solid:face-smile',
              text: 'Dies ist unsere neueste Website-Generation. Sie ist mobil optimiert, schnell und barrierefrei.',
            }
          case 'cms2-portal-mobile':
            return {
              label: 'Vorherige Generation',
              colorClass: 'text-warning',
              icon: 'fa-pro-solid:triangle-exclamation',
              text: `Dieses Produkt ist aus dem Jahr 2018 und damit bereits ${yearsSince2018} Jahre alt. Es fehlen Komfortfunktionen sowie Aspekte der Barrierefreiheit und Geschwindigkeit.`,
              showSupportLink: true,
            }
          case 'cms2-portal':
            return {
              label: 'Veraltete Generation',
              colorClass: 'text-danger',
              icon: 'fa-pro-solid:triangle-exclamation',
              text: `Dieses Produkt ist aus dem Jahre 2014 und damit ${yearsSince2014} Jahre alt. Es bietet keine mobile Optimierung und ist technisch stark veraltet.`,
              showSupportLink: true,
            }
          default:
            return {
              label: type || 'Unbekannt',
              colorClass: 'text-secondary',
              icon: 'fa-pro-solid:circle-question',
              text: 'Für diesen Produkttyp ist keine Beschreibung hinterlegt.',
              showSupportLink: false,
            }
        }
      },
    },
}
</script>

<style scoped lang="scss">
.websites-page {
  display: flex;
  flex-direction: column;
}

.website-offer {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  border: 1px solid #d5e0e9;
  background:
    radial-gradient(circle at top right, rgba(23, 86, 133, 0.1), transparent 42%),
    linear-gradient(180deg, #ffffff, #f8fbfe);
}

.website-offer__hero {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.website-offer__eyebrow {
  margin: 0;
  color: #1d5f8f;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.website-offer__title {
  margin: 0;
  color: #10273d;
  font-size: 1.7rem;
}

.website-offer__lead {
  color: #4a6174;
  max-width: 70ch;
}

.website-offer__pillars {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
}

.website-offer__pillar {
  padding: 0.9rem;
  border: 1px solid #d8e5f1;
  background: #ffffff;
}

.website-offer__pillar h4 {
  margin-bottom: 0.35rem;
  color: #163a58;
  font-size: 1rem;
}

.website-offer__pillar p {
  color: #4a6174;
  font-size: 0.94rem;
}

.website-offer__pricing {
  display: grid;
  grid-template-columns: minmax(220px, 320px) 1fr;
  gap: 1rem;
  align-items: center;
}

.website-offer__price-card {
  padding: 1rem;
  border: 1px solid #c8dcee;
  background: #f2f9ff;
}

.website-offer__price-label {
  color: #31546f;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.website-offer__price-main {
  color: #123557;
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.1;
}

.website-offer__price-sub {
  color: #44627b;
  font-size: 0.9rem;
}

.website-offer__facts {
  margin: 0;
  padding-left: 1.1rem;
  color: #364d61;
}

.website-offer__facts li + li {
  margin-top: 0.4rem;
}

.website-offer__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.type-indicator-btn {
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.popover-close-btn {
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.website-type-popover {
  z-index: 2000;
  width: 18rem;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

@media (max-width: 991px) {
  .website-offer__pillars {
    grid-template-columns: 1fr;
  }

  .website-offer__pricing {
    grid-template-columns: 1fr;
  }
}
</style>
